import React, { type RefCallback, useCallback, forwardRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import InviteTeamIconDI from '@atlaskit/icon/glyph/invite-team';
import PopupDI, { type ContentProps, type TriggerProps } from '@atlaskit/popup';
import AkSpinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { borderRadius, gridSize, layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { inviteDialogEntryPoint } from '@atlassian/jira-servicedesk-invite-team-dialog/entrypoint.tsx';
import { useInviteTeamDialog as useInviteTeamDialogDI } from '@atlassian/jira-servicedesk-invite-team-service/src/services/index.tsx';
import { usePageContext as usePageContextDI } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { USER_JSM_PROJECT_ROLE } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/constants.tsx';
import { InviteTeamDialogTriggerWithRef } from './invite-team-dialog-trigger/index.tsx';
import type { InviteTeamItemProps } from './types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PopupComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
	<InviteTeamDialogWrapper {...props} ref={ref} />
));

const POPUP_OFFSET: [number, number] = [0, 0];

const entryPointParams = {};

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const InviteTeamItem = ({
	children,
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	InviteTeamIcon = InviteTeamIconDI,
	Popup = PopupDI,
	useInviteTeamDialog = useInviteTeamDialogDI,
	usePageContext = usePageContextDI,
	loadEntryPointResource = false,
}: InviteTeamItemProps) => {
	const [{ data: pageContextData }] = usePageContext();
	const { serviceDeskContext } = pageContextData || {};
	const { userRole } = serviceDeskContext || {};
	const [{ isInviteTeamDialogOpen }, { setOnDialogUpdated, setIsInviteTeamDialogOpen }] =
		useInviteTeamDialog();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		inviteDialogEntryPoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isInviteTeamDialogOpen);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		loadEntryPointResource && entryPointActions.load();
	}, [loadEntryPointResource, entryPointActions]);

	const PopupTrigger = useCallback(
		(props: Partial<TriggerProps>) => (
			<InviteTeamDialogTriggerWithRef
				{...props}
				InviteTeamIcon={InviteTeamIcon}
				ref={(element: HTMLElement) => {
					triggerRef(element);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(props.ref as RefCallback<HTMLElement>)(element);
				}}
				isInviteTeamDialogOpen={isInviteTeamDialogOpen}
				setIsInviteTeamDialogOpen={setIsInviteTeamDialogOpen}
			>
				{children}
			</InviteTeamDialogTriggerWithRef>
		),
		[InviteTeamIcon, children, isInviteTeamDialogOpen, setIsInviteTeamDialogOpen, triggerRef],
	);

	const Content = useCallback(
		({ update: onDialogUpdated }: ContentProps) => {
			setOnDialogUpdated(onDialogUpdated);
			return (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="async-servicedesk-invite-team-dialog-experience"
					packageName="jiraServicedeskInviteTeamService"
					runtimeProps={{}}
					fallback={
						<SpinnerContainer>
							<AkSpinner size="large" />
						</SpinnerContainer>
					}
				/>
			);
		},
		[setOnDialogUpdated, entryPointReferenceSubject],
	);

	const onClose = useCallback(() => {
		setIsInviteTeamDialogOpen(false);
	}, [setIsInviteTeamDialogOpen]);

	if (userRole !== USER_JSM_PROJECT_ROLE.AGENT_PROJECT_ADMIN_ROLE) return null;

	if (__SERVER__) {
		return <PopupTrigger aria-expanded={false} aria-haspopup />;
	}

	return (
		<Popup
			isOpen={isInviteTeamDialogOpen}
			onClose={onClose}
			placement="right"
			content={Content}
			trigger={PopupTrigger}
			offset={POPUP_OFFSET}
			popupComponent={PopupComponent}
		/>
	);
};

const backgroundColor = token('elevation.surface.overlay');
const textColor = token('color.text');

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InviteTeamDialogWrapper = styled.div({
	background: backgroundColor,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	color: textColor,
	paddingTop: token('space.200'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 51}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.dialog,
	boxSizing: 'border-box',
	boxShadow: token('elevation.shadow.overlay'),
	'&:focus': {
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 23.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		alignSelf: 'center',
	},
});
